import { FC, KeyboardEvent, useContext, useState, useEffect, useMemo, useRef } from 'react';
import { UserContext } from '../../contexts/UserContext';
import useAuthorization from '@/hooks/useAuthorization';
import ApiV2 from '@/adapters/api-v2';
import styles from './ImpersonationBanner.module.scss';

interface AdminUser {
  id: string;
  email: string;
  name?: string;
  role: string;
  orgId: string;
  orgName?: string;
}

interface AdminUsersResponse {
  success: boolean;
  data?: AdminUser[];
  error?: string;
}

/**
 * ImpersonationBanner component
 *
 * @reason Shows impersonation controls for super_admin users:
 * - When impersonating: shows banner with stop button
 * - When not impersonating: shows user selector dropdown
 */
const ImpersonationBanner: FC = () => {
  const { userState, startImpersonation, stopImpersonation, isImpersonating } =
    useContext(UserContext);
  const { hasRole } = useAuthorization();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((user) => {
      const haystack = `${user.email} ${user.name ?? ''} ${user.orgName ?? user.orgId} ${
        user.role
      }`.toLowerCase();
      return haystack.includes(q);
    });
  }, [users, query]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [query, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Only show for super_admin users
  const isSuperAdmin = hasRole('super_admin');

  // Fetch users list for super_admin (only when not impersonating)
  useEffect(() => {
    if (isSuperAdmin && !isImpersonating) {
      const fetchUsers = async (): Promise<void> => {
        try {
          // Temporarily clear impersonation to get the real user list
          const savedEmail = ApiV2.getImpersonateEmail();
          ApiV2.setImpersonateEmail(null);

          const response = await ApiV2.get<AdminUsersResponse>('/api/admin/users');
          if (response.success && response.data) {
            setUsers(response.data);
          }

          // Restore impersonation email if there was one
          if (savedEmail) {
            ApiV2.setImpersonateEmail(savedEmail);
          }
        } catch (error) {
          console.error('Failed to fetch users for impersonation:', error);
        }
      };

      void fetchUsers();
    }
  }, [isSuperAdmin, isImpersonating]);

  // Handle starting impersonation
  const handleStartImpersonation = async (): Promise<void> => {
    if (!selectedEmail || !startImpersonation) return;

    setIsLoading(true);
    try {
      await startImpersonation(selectedEmail);
      setSelectedEmail('');
      setQuery('');
    } catch (error) {
      console.error('Failed to start impersonation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectUser = (user: AdminUser): void => {
    setSelectedEmail(user.email);
    setQuery(`${user.email} (${user.orgName || user.orgId} - ${user.role})`);
    setIsOpen(false);
  };

  const handleQueryChange = (value: string): void => {
    setQuery(value);
    setSelectedEmail('');
    setIsOpen(true);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setIsOpen(true);
      setHighlightedIndex((i) => Math.min(i + 1, filteredUsers.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (isOpen && filteredUsers[highlightedIndex]) {
        handleSelectUser(filteredUsers[highlightedIndex]);
      } else if (selectedEmail) {
        void handleStartImpersonation();
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // Handle stopping impersonation
  const handleStopImpersonation = (): void => {
    if (stopImpersonation) {
      stopImpersonation();
      // Reload the page to fully reset state
      window.location.reload();
    }
  };

  // Show impersonation banner when actively impersonating
  if (isImpersonating && userState?.impersonatedBy) {
    return (
      <div className={styles.impersonationBanner}>
        <span className={styles.text}>
          <span className={styles.icon}>&#128101;</span>
          Impersonating: <span className={styles.email}>{userState.orgName}</span>
          (as {userState.role})
        </span>
        <button className={styles.stopButton} onClick={handleStopImpersonation}>
          Stop Impersonating
        </button>
      </div>
    );
  }

  // Show user selector for super_admin when not impersonating
  if (isSuperAdmin && !isImpersonating) {
    return (
      <div className={styles.impersonationSelector}>
        <span className={styles.label}>Impersonate user:</span>
        <div className={styles.autocomplete} ref={containerRef}>
          <input
            type="text"
            className={styles.input}
            placeholder="Type to search users..."
            value={query}
            onChange={(e) => handleQueryChange(e.target.value)}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
          />
          {isOpen && filteredUsers.length > 0 && (
            <ul className={styles.options} role="listbox">
              {filteredUsers.map((user, index) => (
                <li
                  key={`${user.orgId}-${user.id}`}
                  role="option"
                  aria-selected={index === highlightedIndex}
                  className={
                    index === highlightedIndex
                      ? `${styles.option} ${styles.optionHighlighted}`
                      : styles.option
                  }
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectUser(user);
                  }}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {user.email} ({user.orgName || user.orgId} - {user.role})
                </li>
              ))}
            </ul>
          )}
          {isOpen && filteredUsers.length === 0 && users.length > 0 && (
            <ul className={styles.options}>
              <li className={styles.optionEmpty}>No users match &ldquo;{query}&rdquo;</li>
            </ul>
          )}
        </div>
        <button
          className={styles.startButton}
          onClick={() => void handleStartImpersonation()}
          disabled={!selectedEmail || isLoading}
        >
          {isLoading ? 'Loading...' : 'Start'}
        </button>
      </div>
    );
  }

  return null;
};

export default ImpersonationBanner;
